import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Star, TrendingUp, Shield, Users, User, Check, ArrowRight, Sparkles, LayoutDashboard, FileText, Search, MessageSquare, Calendar, Award, ChevronRight, ChevronDown, Calculator, Briefcase, GraduationCap, Compass, BookOpen, Send } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Rating } from '../components/ui/Rating';
interface ForAstrologersPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function ForAstrologersPage({
  onNavigate
}: ForAstrologersPageProps) {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const tools = [{
    icon: <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" data-id="element-23071" />,
    bg: 'bg-indigo-50',
    title: 'Личный кабинет астролога',
    desc: 'Панель управления для работы с клиентами.'
  }, {
    icon: <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" data-id="element-23072" />,
    bg: 'bg-violet-50',
    title: 'Астрологические отчёты',
    desc: 'Генерация натальных карт, соляров, транзитов и совместимости.'
  }, {
    icon: <Search className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" data-id="element-23073" />,
    bg: 'bg-emerald-50',
    title: 'Каталог специалистов',
    desc: 'Ваш профиль будет доступен пользователям платформы.'
  }, {
    icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" data-id="element-23074" />,
    bg: 'bg-amber-50',
    title: 'Клиенты и заявки',
    desc: 'Получайте запросы на консультации.'
  }, {
    icon: <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" data-id="element-23075" />,
    bg: 'bg-rose-50',
    title: 'Календарь консультаций',
    desc: 'Удобная система записи на консультации.'
  }, {
    icon: <Award className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" data-id="element-23076" />,
    bg: 'bg-blue-50',
    title: 'Отзывы и рейтинг',
    desc: 'Формируйте репутацию и привлекайте новых клиентов.'
  }];
  const reviews = [{
    name: 'Елена Смирнова',
    exp: 'Опыт 5 лет',
    text: 'Платформа полностью закрыла вопрос с расчетами. Раньше я тратила часы на составление базы для консультации, теперь получаю готовый PDF за секунды и фокусируюсь на общении с клиентом.',
    rating: 5
  }, {
    name: 'Михаил Волков',
    exp: 'Опыт 8 лет',
    text: 'Отличный поток новых клиентов из каталога. Я купил пакет PRO и он окупился в первую же неделю работы. Очень удобный личный кабинет и система записи.',
    rating: 5
  }, {
    name: 'Анна Коваль',
    exp: 'Начинающий астролог',
    text: 'Для старта практики это идеальное место. Не нужно делать свой сайт, настраивать оплату — всё уже готово. Отчёты получаются очень подробными и профессиональными.',
    rating: 5
  }];
  return <div className="w-full bg-white font-sans overflow-hidden" data-id="element-23077">
      {/* БЛОК 1 — HERO */}
      <section className="relative pt-14 pb-16 sm:pt-24 sm:pb-32 lg:pt-32 lg:pb-40 bg-[#0a1628] text-white overflow-hidden" data-id="element-23078">
        {/* Cosmic Background Elements */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-23079">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-900/40 via-[#0a1628] to-[#0a1628]" data-id="element-23080"></div>
          <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/20 blur-[120px]" data-id="element-23081"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" data-id="element-23082"></div>
          {[...Array(30)].map((_, i) => <div key={i} className="absolute rounded-full bg-white" style={{
          width: `${Math.random() * 2 + 1}px`,
          height: `${Math.random() * 2 + 1}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          opacity: Math.random() * 0.5 + 0.1,
          animation: `pulse ${Math.random() * 3 + 2}s infinite ${Math.random() * 2}s`
        }} data-id="element-23083" />)}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23084">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-23085">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-23086">
              <motion.div variants={fadeUp} data-id="element-23087">
                <Badge variant="pro" className="mb-3 sm:mb-6 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30" data-id="element-23088">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" data-id="element-23089" />
                  Для специалистов
                </Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-3 sm:mb-6 leading-[1.1] tracking-tight" data-id="element-23090">
                Платформа для астрологов{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400" data-id="element-23091">
                  нового поколения
                </span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-sm sm:text-lg md:text-xl text-slate-300 mb-5 sm:mb-8 leading-relaxed font-light" data-id="element-23092">
                Создавайте натальные карты, получайте клиентов и развивайте свою
                астрологическую практику на одной платформе.
              </motion.p>

              <motion.ul variants={staggerContainer} className="space-y-2 sm:space-y-3 mb-6 sm:mb-10" data-id="element-23093">
                {['готовые астрологические инструменты', 'поток клиентов через платформу', 'удобный личный кабинет', 'возможность зарабатывать онлайн'].map((item, i) => <motion.li key={i} variants={fadeUp} className="flex items-center text-slate-200 text-xs sm:text-base" data-id="element-23094">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-indigo-500/20 flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0" data-id="element-23095">
                      <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-indigo-400" data-id="element-23096" />
                    </div>
                    {item}
                  </motion.li>)}
              </motion.ul>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-2.5 sm:gap-4" data-id="element-23097">
                <Button size="lg" variant="outline" className="!bg-white !text-[#0a1628] hover:!bg-slate-200 !border-none px-5 sm:px-8 py-2.5 sm:py-4 text-xs sm:text-sm font-bold uppercase tracking-wider" onClick={() => document.getElementById('apply-form')?.scrollIntoView({
                behavior: 'smooth'
              })} data-id="element-23098">
                  Стать астрологом
                </Button>
                <Button size="lg" variant="outline" className="!border-slate-500 !text-white hover:!bg-white/10 px-5 sm:px-8 py-2.5 sm:py-4 text-xs sm:text-sm font-bold uppercase tracking-wider" data-id="element-23099">
                  Посмотреть возможности
                </Button>
              </motion.div>
            </motion.div>

            {/* Abstract Interface Illustration — desktop only */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            rotate: 2
          }} animate={{
            opacity: 1,
            scale: 1,
            rotate: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="hidden lg:block relative" data-id="element-23100">
              <div className="relative rounded-2xl bg-slate-800/50 border border-slate-700/50 backdrop-blur-xl p-6 shadow-2xl overflow-hidden" data-id="element-23101">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-violet-500" data-id="element-23102"></div>
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-700/50" data-id="element-23103">
                  <div className="flex items-center gap-3" data-id="element-23104">
                    <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center" data-id="element-23105">
                      <User className="w-5 h-5 text-indigo-400" data-id="element-23106" />
                    </div>
                    <div data-id="element-23107">
                      <div className="h-3 w-24 bg-slate-600 rounded mb-2" data-id="element-23108"></div>
                      <div className="h-2 w-16 bg-slate-700 rounded" data-id="element-23109"></div>
                    </div>
                  </div>
                  <div className="h-6 w-20 bg-emerald-500/20 rounded-full border border-emerald-500/30" data-id="element-23110"></div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-23111">
                  <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-600/30" data-id="element-23112">
                    <div className="h-2 w-12 bg-slate-500 rounded mb-3" data-id="element-23113"></div>
                    <div className="h-6 w-20 bg-white/90 rounded mb-1" data-id="element-23114"></div>
                  </div>
                  <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-600/30" data-id="element-23115">
                    <div className="h-2 w-12 bg-slate-500 rounded mb-3" data-id="element-23116"></div>
                    <div className="h-6 w-20 bg-indigo-400 rounded mb-1" data-id="element-23117"></div>
                  </div>
                </div>
                <div className="space-y-3" data-id="element-23118">
                  {[1, 2, 3].map(i => <div key={i} className="flex items-center justify-between bg-slate-700/20 p-3 rounded-lg border border-slate-600/20" data-id="element-23119">
                      <div className="flex items-center gap-3" data-id="element-23120">
                        <div className="w-8 h-8 rounded-full bg-slate-600/50" data-id="element-23121"></div>
                        <div className="h-2 w-32 bg-slate-500 rounded" data-id="element-23122"></div>
                      </div>
                      <div className="h-2 w-12 bg-slate-600 rounded" data-id="element-23123"></div>
                    </div>)}
                </div>
              </div>
              <motion.div animate={{
              y: [-10, 10, -10]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -right-8 top-12 bg-white rounded-xl p-4 shadow-xl border border-slate-100" data-id="element-23124">
                <div className="flex items-center gap-3" data-id="element-23125">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-23126">
                    <Check className="w-4 h-4 text-emerald-600" data-id="element-23127" />
                  </div>
                  <div data-id="element-23128">
                    <div className="text-xs font-bold text-slate-900" data-id="element-23129">
                      Новая заявка
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-23130">
                      Консультация • 5000 ₽
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* БЛОК 2 — ИДЕЯ ПЛАТФОРМЫ */}
      <section className="py-10 sm:py-20 lg:py-32 bg-slate-50" data-id="element-23131">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23132">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-23133">
            {/* Illustration — hidden on mobile */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUp} className="order-2 lg:order-1 relative hidden md:block" data-id="element-23134">
              <div className="aspect-square max-w-sm lg:max-w-md mx-auto relative" data-id="element-23135">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-100 to-violet-50 rounded-full animate-spin-slow opacity-70" data-id="element-23136"></div>
                <div className="absolute inset-8 bg-white rounded-full shadow-xl flex items-center justify-center border border-slate-100 z-10" data-id="element-23137">
                  <div className="grid grid-cols-2 gap-4 p-8 w-full h-full" data-id="element-23138">
                    <div className="bg-indigo-50 rounded-2xl flex flex-col items-center justify-center text-center p-4" data-id="element-23139">
                      <FileText className="w-8 h-8 text-indigo-500 mb-2" data-id="element-23140" />
                      <span className="text-xs font-semibold text-indigo-900" data-id="element-23141">
                        Отчёты
                      </span>
                    </div>
                    <div className="bg-violet-50 rounded-2xl flex flex-col items-center justify-center text-center p-4" data-id="element-23142">
                      <MessageSquare className="w-8 h-8 text-violet-500 mb-2" data-id="element-23143" />
                      <span className="text-xs font-semibold text-violet-900" data-id="element-23144">
                        Консультации
                      </span>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl flex flex-col items-center justify-center text-center p-4" data-id="element-23145">
                      <Briefcase className="w-8 h-8 text-emerald-500 mb-2" data-id="element-23146" />
                      <span className="text-xs font-semibold text-emerald-900" data-id="element-23147">
                        Услуги
                      </span>
                    </div>
                    <div className="bg-amber-50 rounded-2xl flex flex-col items-center justify-center text-center p-4" data-id="element-23148">
                      <Users className="w-8 h-8 text-amber-500 mb-2" data-id="element-23149" />
                      <span className="text-xs font-semibold text-amber-900" data-id="element-23150">
                        Клиенты
                      </span>
                    </div>
                  </div>
                </div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-slate-900 rounded-full shadow-2xl z-20 flex items-center justify-center border-4 border-white" data-id="element-23151">
                  <Star className="w-8 h-8 text-white" data-id="element-23152" />
                </div>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="order-1 lg:order-2" data-id="element-23153">
              <motion.h2 variants={fadeUp} className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-6" data-id="element-23154">
                Ваш цифровой кабинет астролога
              </motion.h2>
              <motion.p variants={fadeUp} className="text-sm sm:text-lg text-slate-600 mb-5 sm:mb-8 leading-relaxed" data-id="element-23155">
                Мы создали платформу, которая объединяет профессиональных
                астрологов и людей, интересующихся астрологией.
              </motion.p>

              <motion.div variants={fadeUp} data-id="element-23156">
                <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-2.5 sm:mb-4" data-id="element-23157">
                  Вы можете:
                </h3>
                <ul className="space-y-2 sm:space-y-4" data-id="element-23158">
                  {['создавать астрологические отчёты', 'проводить консультации', 'публиковать свои услуги', 'получать новых клиентов'].map((item, i) => <li key={i} className="flex items-center text-slate-700 bg-white p-2.5 sm:p-4 rounded-xl shadow-sm border border-slate-100" data-id="element-23159">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-indigo-50 flex items-center justify-center mr-2.5 sm:mr-4 flex-shrink-0" data-id="element-23160">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600" data-id="element-23161" />
                      </div>
                      <span className="font-medium text-xs sm:text-base" data-id="element-23162">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* БЛОК 3 — ЧТО ВЫ ПОЛУЧАЕТЕ */}
      <section className="py-10 sm:py-20 lg:py-32 bg-white" data-id="element-23163">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23164">
          <div className="text-center mb-6 sm:mb-16" data-id="element-23165">
            <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-23166">
              Инструменты для работы астролога
            </h2>
            <p className="text-xs sm:text-lg text-slate-500 max-w-2xl mx-auto" data-id="element-23167">
              Всё необходимое для ведения профессиональной практики.
            </p>
          </div>

          {/* Mobile: compact list */}
          <div className="sm:hidden space-y-2" data-id="element-23168">
            {tools.map((tool, i) => <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100" data-id="element-23169">
                <div className={`w-10 h-10 rounded-xl ${tool.bg} flex items-center justify-center flex-shrink-0`} data-id="element-23170">
                  {tool.icon}
                </div>
                <div className="min-w-0" data-id="element-23171">
                  <h3 className="text-sm font-semibold text-slate-900 leading-tight" data-id="element-23172">
                    {tool.title}
                  </h3>
                  <p className="text-xs text-slate-500 leading-snug mt-0.5" data-id="element-23173">
                    {tool.desc}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Desktop: card grid */}
          <motion.div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} data-id="element-23174">
            {tools.map((tool, i) => <motion.div key={i} variants={fadeUp} data-id="element-23175">
                <Card hoverable padding="lg" className="h-full border-slate-100" data-id="element-23176">
                  <div className={`w-14 h-14 rounded-2xl ${tool.bg} flex items-center justify-center mb-6`} data-id="element-23177">
                    {tool.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-23178">
                    {tool.title}
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed" data-id="element-23179">
                    {tool.desc}
                  </p>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* БЛОК 4 — КАК РАБОТАЕТ ПЛАТФОРМА */}
      <section className="py-10 sm:py-20 lg:py-32 bg-slate-900 text-white overflow-hidden" data-id="element-23180">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-23181">
          <div className="text-center mb-6 sm:mb-16" data-id="element-23182">
            <h2 className="text-xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-4" data-id="element-23183">
              Как начать работу
            </h2>
            <p className="text-xs sm:text-lg text-slate-400" data-id="element-23184">
              Простой путь от регистрации до первых клиентов
            </p>
          </div>

          <div className="relative" data-id="element-23185">
            <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-800 -translate-y-1/2 z-0" data-id="element-23186"></div>

            {/* Mobile: vertical numbered list */}
            <div className="sm:hidden space-y-3" data-id="element-23187">
              {['Создайте профиль астролога', 'Опишите специализацию и услуги', 'Выберите пакет отчётов', 'Получайте заявки от клиентов', 'Консультируйте и зарабатывайте'].map((title, i) => <div key={i} className="flex items-center gap-3" data-id="element-23188">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-[0_0_20px_rgba(79,70,229,0.3)]" data-id="element-23189">
                    {i + 1}
                  </div>
                  <span className="text-sm text-slate-200" data-id="element-23190">{title}</span>
                </div>)}
            </div>

            {/* Desktop: horizontal steps */}
            <motion.div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-8 relative z-10" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-23191">
              {[{
              step: '1',
              title: 'Создайте профиль астролога'
            }, {
              step: '2',
              title: 'Опишите специализацию и услуги'
            }, {
              step: '3',
              title: 'Выберите пакет отчётов'
            }, {
              step: '4',
              title: 'Получайте заявки от клиентов'
            }, {
              step: '5',
              title: 'Консультируйте и зарабатывайте'
            }].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex flex-col items-center text-center" data-id="element-23192">
                  <div className="w-16 h-16 rounded-full bg-indigo-600 flex items-center justify-center text-2xl font-bold mb-6 shadow-[0_0_30px_rgba(79,70,229,0.3)] relative z-10 border-4 border-slate-900" data-id="element-23193">
                    {item.step}
                  </div>
                  <h3 className="text-sm lg:text-base font-medium text-slate-200" data-id="element-23194">
                    {item.title}
                  </h3>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* БЛОК 5 — ПАКЕТЫ ДЛЯ АСТРОЛОГОВ */}
      <section className="py-10 sm:py-20 lg:py-32 bg-slate-50" data-id="element-23195">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23196">
          <div className="text-center mb-6 sm:mb-16" data-id="element-23197">
            <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-6" data-id="element-23198">
              Пакеты отчётов для профессиональной работы
            </h2>
            <div className="max-w-2xl mx-auto bg-white p-3 sm:p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-23199">
              <p className="text-slate-600 font-medium mb-2 sm:mb-4 text-xs sm:text-base" data-id="element-23200">
                Каждый отчёт можно использовать для:
              </p>
              <div className="flex flex-wrap justify-center gap-1.5 sm:gap-3" data-id="element-23201">
                {['натальной карты', 'соляра', 'транзитов', 'совместимости'].map((type, i) => <Badge key={i} variant="neutral" className="bg-slate-50 text-slate-700 py-0.5 sm:py-1.5 px-1.5 sm:px-3 text-[10px] sm:text-sm" data-id="element-23202">
                    {type}
                  </Badge>)}
              </div>
            </div>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 max-w-5xl mx-auto items-stretch" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-23203">
            {/* START */}
            <motion.div variants={fadeUp} className="flex" data-id="element-23204">
              <Card padding="lg" className="border-slate-200 flex flex-col w-full !p-4 sm:!p-6 lg:!p-8" data-id="element-23205">
                <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-slate-100" data-id="element-23206">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-0.5 sm:mb-1" data-id="element-23207">
                    START
                  </h3>
                  <p className="text-slate-500 text-xs sm:text-base" data-id="element-23208">
                    Подходит для начинающих астрологов.
                  </p>
                </div>
                <div className="mb-4 sm:mb-6" data-id="element-23209">
                  <div className="flex items-baseline gap-1 mb-0.5 sm:mb-1" data-id="element-23210">
                    <span className="text-2xl sm:text-4xl font-bold text-slate-900" data-id="element-23211">
                      9 900
                    </span>
                    <span className="text-base sm:text-lg font-semibold text-slate-500" data-id="element-23212">
                      ₽
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3" data-id="element-23213">
                    <span className="text-indigo-600 font-semibold text-xs sm:text-base" data-id="element-23214">
                      20 отчётов
                    </span>
                    <span className="text-slate-400 text-[10px] sm:text-sm" data-id="element-23215">
                      ≈ 495 ₽ / отчёт
                    </span>
                  </div>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-5 sm:mb-8 flex-grow" data-id="element-23216">
                  {['генерация астрологических отчётов', 'построение натальных карт', 'солярный прогноз', 'анализ совместимости', 'прогноз транзитов'].map((f, i) => <li key={i} className="flex items-start text-xs sm:text-sm text-slate-600" data-id="element-23217">
                      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23218" />
                      <span data-id="element-23219">{f}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-23220">
                  <Button fullWidth variant="outline" className="border-slate-300 text-xs sm:text-sm" data-id="element-23221">
                    Выбрать пакет
                  </Button>
                </div>
              </Card>
            </motion.div>

            {/* PRO */}
            <motion.div variants={fadeUp} className="relative flex" data-id="element-23222">
              <div className="absolute -inset-[2px] bg-gradient-to-b from-indigo-500 to-violet-600 rounded-[2rem] blur-sm opacity-50" data-id="element-23223"></div>
              <Card padding="lg" className="relative border-none shadow-2xl ring-1 ring-indigo-500/50 flex flex-col w-full !p-4 sm:!p-6 lg:!p-8" data-id="element-23224">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-23225">
                  <Badge className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white border-none shadow-lg px-3 sm:px-4 py-0.5 sm:py-1 text-[10px] sm:text-sm" data-id="element-23226">
                    <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" data-id="element-23227" />
                    Самый популярный
                  </Badge>
                </div>
                <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-indigo-100 mt-1 sm:mt-2" data-id="element-23228">
                  <h3 className="text-xl sm:text-2xl font-bold text-indigo-900 mb-0.5 sm:mb-1" data-id="element-23229">
                    PRO
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-base" data-id="element-23230">
                    Оптимальный пакет для регулярной работы.
                  </p>
                </div>
                <div className="mb-4 sm:mb-6" data-id="element-23231">
                  <div className="flex items-baseline gap-1 mb-0.5 sm:mb-1" data-id="element-23232">
                    <span className="text-3xl sm:text-5xl font-bold text-slate-900" data-id="element-23233">
                      19 900
                    </span>
                    <span className="text-base sm:text-lg font-semibold text-slate-500" data-id="element-23234">
                      ₽
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3" data-id="element-23235">
                    <span className="text-indigo-600 font-bold text-xs sm:text-base" data-id="element-23236">
                      50 отчётов
                    </span>
                    <span className="text-slate-400 text-[10px] sm:text-sm" data-id="element-23237">
                      ≈ 398 ₽ / отчёт
                    </span>
                  </div>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-5 sm:mb-8 flex-grow" data-id="element-23238">
                  {['генерация всех типов отчётов', 'быстрые расчёты натальных карт', 'солярные прогнозы', 'анализ совместимости', 'транзитные прогнозы', 'приоритетная поддержка'].map((f, i) => <li key={i} className="flex items-start text-xs sm:text-sm text-slate-700" data-id="element-23239">
                      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23240" />
                      <span data-id="element-23241">{f}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-23242">
                  <Button fullWidth variant="primary" className="py-2.5 sm:py-3 text-xs sm:text-base shadow-lg shadow-indigo-500/30" data-id="element-23243">
                    Выбрать пакет
                  </Button>
                </div>
              </Card>
            </motion.div>

            {/* EXPERT */}
            <motion.div variants={fadeUp} className="flex" data-id="element-23244">
              <Card padding="lg" className="border-slate-200 flex flex-col w-full !p-4 sm:!p-6 lg:!p-8" data-id="element-23245">
                <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-slate-100" data-id="element-23246">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-0.5 sm:mb-1" data-id="element-23247">
                    EXPERT
                  </h3>
                  <p className="text-slate-500 text-xs sm:text-base" data-id="element-23248">
                    Для активной практики и большого потока клиентов.
                  </p>
                </div>
                <div className="mb-4 sm:mb-6" data-id="element-23249">
                  <div className="flex items-baseline gap-1 mb-0.5 sm:mb-1" data-id="element-23250">
                    <span className="text-2xl sm:text-4xl font-bold text-slate-900" data-id="element-23251">
                      34 900
                    </span>
                    <span className="text-base sm:text-lg font-semibold text-slate-500" data-id="element-23252">
                      ₽
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3" data-id="element-23253">
                    <span className="text-indigo-600 font-semibold text-xs sm:text-base" data-id="element-23254">
                      100 отчётов
                    </span>
                    <span className="text-slate-400 text-[10px] sm:text-sm" data-id="element-23255">
                      ≈ 349 ₽ / отчёт
                    </span>
                  </div>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-5 sm:mb-8 flex-grow" data-id="element-23256">
                  {['генерация всех типов отчётов', 'быстрые расчёты натальных карт', 'солярные прогнозы', 'анализ совместимости', 'транзитные прогнозы', 'приоритетная поддержка'].map((f, i) => <li key={i} className="flex items-start text-xs sm:text-sm text-slate-600" data-id="element-23257">
                      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23258" />
                      <span data-id="element-23259">{f}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-23260">
                  <Button fullWidth variant="outline" className="border-slate-300 text-xs sm:text-sm" data-id="element-23261">
                    Выбрать пакет
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* БЛОК 6 — ПРИМЕР ДОХОДА */}
      <section className="py-10 sm:py-20 bg-white" data-id="element-23262">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23263">
          <motion.div className="bg-gradient-to-br from-indigo-900 to-slate-900 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-23264">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-23265">
              <div className="p-5 sm:p-10 lg:p-12 flex flex-col justify-center" data-id="element-23266">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-3 sm:mb-6" data-id="element-23267">
                  <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400" data-id="element-23268" />
                </div>
                <h2 className="text-xl sm:text-3xl font-bold text-white mb-2 sm:mb-4" data-id="element-23269">
                  Пример дохода астролога
                </h2>
                <p className="text-indigo-200 leading-relaxed text-xs sm:text-base" data-id="element-23270">
                  Экономика работы на платформе: небольшая инвестиция в отчёт —
                  высокая маржинальность с каждой консультации.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-5 sm:p-10 lg:p-12 flex flex-col justify-center border-t md:border-t-0 md:border-l border-white/10" data-id="element-23271">
                <table className="w-full" data-id="element-23272">
                  <tbody data-id="element-23273">
                    <tr className="border-b border-white/10" data-id="element-23274">
                      <td className="text-slate-300 text-xs sm:text-base py-2.5 sm:py-4 pr-4 align-middle" data-id="element-23275">
                        Консультация
                      </td>
                      <td className="text-right py-2.5 sm:py-4 align-middle whitespace-nowrap" data-id="element-23276">
                        <span className="text-lg sm:text-2xl font-semibold text-white" data-id="element-23277">
                          5 000 ₽
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-white/10" data-id="element-23278">
                      <td className="text-slate-300 text-xs sm:text-base py-2.5 sm:py-4 pr-4 align-middle" data-id="element-23279">
                        Стоимость отчёта
                      </td>
                      <td className="text-right py-2.5 sm:py-4 align-middle whitespace-nowrap" data-id="element-23280">
                        <span className="text-base sm:text-xl font-semibold text-rose-400" data-id="element-23281">
                          − 350 ₽
                        </span>
                      </td>
                    </tr>
                    <tr data-id="element-23282">
                      <td className="text-white font-bold text-xs sm:text-base pt-3 sm:pt-5 pr-4 align-middle" data-id="element-23283">
                        Доход астролога
                      </td>
                      <td className="text-right pt-3 sm:pt-5 align-middle whitespace-nowrap" data-id="element-23284">
                        <span className="text-xl sm:text-3xl font-bold text-emerald-400" data-id="element-23285">
                          4 650 ₽
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* БЛОК 7 — ДЛЯ КОГО ЭТА ПЛАТФОРМА */}
      <section className="py-10 sm:py-20 lg:py-32 bg-slate-50" data-id="element-23286">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23287">
          <div className="text-center mb-5 sm:mb-16" data-id="element-23288">
            <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900" data-id="element-23289">
              Кому подойдёт платформа
            </h2>
          </div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-6 max-w-5xl mx-auto" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-23290">
            {[{
            title: 'Практикующим астрологам',
            icon: <Star className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-23291" />
          }, {
            title: 'Консультантам по натальным картам',
            icon: <Compass className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-23292" />
          }, {
            title: 'Специалистам по прогнозам',
            icon: <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-23293" />
          }, {
            title: 'Начинающим астрологам',
            icon: <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-23294" />
          }, {
            title: 'Астрологическим школам',
            icon: <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-23295" />
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className={i === 4 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-23296">
                <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-slate-100 flex items-center gap-2.5 sm:gap-4 h-full" data-id="element-23297">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0" data-id="element-23298">
                    {item.icon}
                  </div>
                  <span className="font-semibold text-slate-800 text-xs sm:text-base" data-id="element-23299">
                    {item.title}
                  </span>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* БЛОК 8 — ОТЗЫВЫ */}
      <section className="py-10 sm:py-20 lg:py-32 bg-white" data-id="element-23300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23301">
          <div className="text-center mb-5 sm:mb-16" data-id="element-23302">
            <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900" data-id="element-23303">
              Астрологи о платформе
            </h2>
          </div>

          {/* Mobile: show 1 review + expand button */}
          <div className="sm:hidden space-y-3" data-id="element-23304">
            {(showAllReviews ? reviews : reviews.slice(0, 1)).map((review, i) => <Card key={i} padding="lg" className="bg-slate-50 border-none !p-4" data-id="element-23305">
                  <Rating value={review.rating} showCount={false} className="mb-2" data-id="element-23306" />
                  <p className="text-slate-700 mb-3 italic text-xs leading-relaxed" data-id="element-23307">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-2.5" data-id="element-23308">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-700 text-xs" data-id="element-23309">
                      {review.name.charAt(0)}
                    </div>
                    <div data-id="element-23310">
                      <div className="font-bold text-slate-900 text-xs" data-id="element-23311">
                        {review.name}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-23312">
                        {review.exp}
                      </div>
                    </div>
                  </div>
                </Card>)}
            {!showAllReviews && <button onClick={() => setShowAllReviews(true)} className="w-full flex items-center justify-center gap-1.5 text-indigo-600 font-medium text-xs py-2" data-id="element-23313">
                Показать все отзывы
                <ChevronDown className="w-3.5 h-3.5" data-id="element-23314" />
              </button>}
          </div>

          {/* Desktop: grid */}
          <motion.div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-23315">
            {reviews.map((review, i) => <motion.div key={i} variants={fadeUp} data-id="element-23316">
                <Card padding="lg" className="h-full bg-slate-50 border-none" data-id="element-23317">
                  <Rating value={review.rating} showCount={false} className="mb-4" data-id="element-23318" />
                  <p className="text-slate-700 mb-6 italic text-base leading-relaxed" data-id="element-23319">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3" data-id="element-23320">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-700 text-sm" data-id="element-23321">
                      {review.name.charAt(0)}
                    </div>
                    <div data-id="element-23322">
                      <div className="font-bold text-slate-900 text-sm" data-id="element-23323">
                        {review.name}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-23324">{review.exp}</div>
                    </div>
                  </div>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* БЛОК 9 — ФОРМА РЕГИСТРАЦИИ + CTA */}
      <section id="apply-form" className="py-10 sm:py-24 bg-[#0a1628] text-white relative overflow-hidden" data-id="element-23325">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-900/30 via-[#0a1628] to-[#0a1628]" data-id="element-23326"></div>
        {[...Array(20)].map((_, i) => <div key={i} className="absolute rounded-full bg-white pointer-events-none" style={{
        width: `${Math.random() * 2 + 1}px`,
        height: `${Math.random() * 2 + 1}px`,
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        opacity: Math.random() * 0.4 + 0.1
      }} data-id="element-23327" />)}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23328">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-6 sm:mb-12" data-id="element-23329">
            <div className="w-14 h-14 sm:w-20 sm:h-20 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-8" data-id="element-23330">
              <Sparkles className="w-7 h-7 sm:w-10 sm:h-10 text-indigo-400" data-id="element-23331" />
            </div>
            <h2 className="text-xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-6" data-id="element-23332">
              Начните принимать клиентов уже сегодня
            </h2>
            <p className="text-xs sm:text-lg text-slate-300 max-w-xl mx-auto" data-id="element-23333">
              Создайте профиль астролога. Мы рассматриваем заявки в течение 3
              рабочих дней.
            </p>
          </motion.div>

          {!formSubmitted ? <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/10 p-4 sm:p-8 lg:p-10" data-id="element-23334">
              <form className="space-y-3.5 sm:space-y-6" onSubmit={e => {
            e.preventDefault();
            setFormSubmitted(true);
          }} data-id="element-23335">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6" data-id="element-23336">
                  <div data-id="element-23337">
                    <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1 sm:mb-1.5" data-id="element-23338">
                      Имя Фамилия
                    </label>
                    <input type="text" className="w-full rounded-lg sm:rounded-xl bg-white/10 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors text-xs sm:text-base" placeholder="Иван Иванов" required data-id="element-23339" />
                  </div>
                  <div data-id="element-23340">
                    <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1 sm:mb-1.5" data-id="element-23341">
                      Email
                    </label>
                    <input type="email" className="w-full rounded-lg sm:rounded-xl bg-white/10 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors text-xs sm:text-base" placeholder="ivan@example.com" required data-id="element-23342" />
                  </div>
                </div>
                <div data-id="element-23343">
                  <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1 sm:mb-1.5" data-id="element-23344">
                    Ссылка на соцсети / сайт
                  </label>
                  <input type="url" className="w-full rounded-lg sm:rounded-xl bg-white/10 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors text-xs sm:text-base" placeholder="https://instagram.com/..." data-id="element-23345" />
                </div>
                <div data-id="element-23346">
                  <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1 sm:mb-1.5" data-id="element-23347">
                    Опыт работы (кратко)
                  </label>
                  <textarea rows={3} className="w-full rounded-lg sm:rounded-xl bg-white/10 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors resize-none text-xs sm:text-base" placeholder="Опишите ваш опыт..." data-id="element-23348"></textarea>
                </div>
                <Button size="lg" fullWidth variant="outline" type="submit" className="!bg-white !text-[#0a1628] hover:!bg-slate-200 !border-none py-2.5 sm:py-4 text-xs sm:text-base font-bold uppercase tracking-wider" data-id="element-23349">
                  <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" data-id="element-23350" />
                  Отправить заявку
                </Button>
              </form>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/10 p-6 sm:p-12 text-center" data-id="element-23351">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4 sm:mb-6" data-id="element-23352">
                <Check className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400" data-id="element-23353" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3" data-id="element-23354">
                Заявка отправлена!
              </h3>
              <p className="text-slate-300 text-xs sm:text-base" data-id="element-23355">
                Мы рассмотрим вашу заявку в течение 3 рабочих дней и свяжемся с
                вами по email.
              </p>
            </motion.div>}
        </div>
      </section>
    </div>;
}